import { useCallback, useEffect, useState } from 'react';
import Raven from 'raven-js';
import { useMutation, useQuery } from 'data-fetching-client';
import unescapedText from 'I18n/utils/unescapedText';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import { ReviewWizard } from 'trellis-ecosystem';
import EcosystemReview from '../../records/Review';
import { SUBMIT_REVIEW } from '../../queries/submitReviewQuery';
import { EDIT_REVIEW } from '../../queries/editReviewQuery';
import { GET_USER_INSIGHTS } from '../../queries/userInsightsQuery';
import { GET_DISPLAY_NAME } from '../../queries/reviewerDisplayNameQuery';
import { jsx as _jsx } from "react/jsx-runtime";
export function EcosystemReviewWizard({
  children,
  customValidationErrors,
  defaultReview,
  HeaderComponent,
  isEditing,
  editReason,
  offeringId,
  onChange,
  onClose,
  onFail,
  onSuccess,
  originalReview,
  reviewFormatter,
  reviewId,
  reviewType,
  showLegalCheckbox,
  validationSchema
}) {
  const [submitReview] = useMutation(SUBMIT_REVIEW);
  const [editReview] = useMutation(EDIT_REVIEW);
  const [hasEdits, setHasEdits] = useState(false);
  const [displayName, setDisplayName] = useState(null);
  const [employees, setEmployees] = useState(null);
  const [marketplaceIndustry, setMarketplaceIndustry] = useState(null);
  const {
    data: displayNameData
  } = useQuery(GET_DISPLAY_NAME);
  const {
    data: userInsightsData
  } = useQuery(GET_USER_INSIGHTS);
  useEffect(() => {
    if (displayNameData) {
      const {
        displayName: {
          displayName: fetchedDisplayName
        }
      } = displayNameData;
      setDisplayName(fetchedDisplayName);
    }
  }, [displayNameData]);
  useEffect(() => {
    if (userInsightsData) {
      const {
        employeeSize,
        industry: fetchedIndustry
      } = userInsightsData.userInsights.portalInsights;
      setEmployees(employeeSize || null);
      setMarketplaceIndustry(fetchedIndustry || null);
    }
  }, [userInsightsData]);
  const handleChange = useCallback(review => {
    if (isEditing && originalReview) {
      let hasChanges = false;
      Object.entries(review).forEach(([typeKey, typeObj]) => {
        Object.entries(typeObj).forEach(([key, value]) => {
          if (!originalReview[typeKey][key] && value) {
            hasChanges = true;
          } else if (value && originalReview[typeKey][key] !== value) {
            hasChanges = true;
          }
        });
      });
      setHasEdits(hasChanges);
    }
    if (onChange) {
      onChange(review);
    }
  }, [isEditing, onChange, originalReview]);
  const handleSubmit = useCallback(review => {
    let filteredReview = Object.assign({}, review);
    if (reviewFormatter) {
      filteredReview = reviewFormatter(review);
    }
    let submittedReview = new EcosystemReview(Object.assign({}, filteredReview, {
      answers: filteredReview.content
    })).set('createdAt', new Date().valueOf()).set('entityId', offeringId).set('reviewType', reviewType).set('reviewerDisplayName', displayName).set('employees', employees).set('marketplaceIndustry', marketplaceIndustry);
    if (isEditing && reviewId) {
      submittedReview = submittedReview.set('id', reviewId);
    }
    const submitFunction = isEditing ? editReview : submitReview;
    submitFunction({
      variables: {
        review: submittedReview,
        editReason
      }
    }).then(() => {
      // Success alert
      FloatingAlertStore.addAlert({
        message: unescapedText('marketplace-ui-reviews.wizard.alert.submit.message'),
        titleText: unescapedText('marketplace-ui-reviews.wizard.alert.submit.title'),
        type: 'success'
      });
      if (onSuccess) {
        onSuccess(filteredReview);
      }
    }).catch(e => {
      // Failure alert
      let messageKey = 'marketplace-ui-reviews.wizard.alert.error.message';
      try {
        const error = JSON.parse(e.data).subCategory;
        if (isValidI18nKey(`marketplace-ui-reviews.error.${error}`)) {
          messageKey = `marketplace-ui-reviews.error.${error}`;
        }
      } catch (err) {
        Raven.captureMessage('subCategory not provided in backend error response ', {
          level: 'warning',
          extra: {
            error: err
          }
        });
      }
      FloatingAlertStore.addAlert({
        message: unescapedText(messageKey),
        titleText: unescapedText('marketplace-ui-reviews.wizard.alert.error.title'),
        type: 'danger'
      });
      if (onFail) {
        onFail();
      }
    });
  }, [displayName, editReason, editReview, employees, isEditing, offeringId, marketplaceIndustry, onFail, onSuccess, reviewFormatter, reviewId, reviewType, submitReview]);
  const fullCustomValidationErrors = customValidationErrors ? [...customValidationErrors] : [];
  if (isEditing && !hasEdits) {
    fullCustomValidationErrors.push(unescapedText('marketplace-ui-reviews.wizard.customValidation.noEdits'));
  }
  if (isEditing && !editReason) {
    fullCustomValidationErrors.push(unescapedText('marketplace-ui-reviews.wizard.customValidation.noEditReason'));
  }
  return /*#__PURE__*/_jsx(ReviewWizard, {
    customValidationErrors: fullCustomValidationErrors,
    defaultReview: defaultReview,
    HeaderComponent: HeaderComponent,
    onChange: handleChange,
    onClose: onClose,
    onSubmit: handleSubmit,
    showLegalCheckbox: showLegalCheckbox,
    validationSchema: validationSchema,
    children: children
  });
}